<template>
  <div
    class="modal-payment-plan d-flex flex-column align-content-end justify-content-start"
  >
    <div class="modal-title mt-3">
      {{
        t("CL_WITHDRAWAL.WITHDRAWAL_TO_PAY_OVER_WEEKS", {
          weeks: paymentTerm,
        })
      }}
    </div>
    <hr class="modal-divider" />
    <div class="modal-row">
      <span class="modal-label">
        {{ t("CL_WITHDRAWAL.WITHDRAWAL_MINIMUM_WEEKLY_PAYMENTS") }}
      </span>
      <span class="modal-value">{{ weeklyPaymentAmount }}</span>
    </div>
    <hr class="modal-divider" />
    <div class="modal-row">
      <span class="modal-label">
        {{ t("CL_WITHDRAWAL.WITHDRAWAL_FIRST_PAYMENT") }}
      </span>
      <span class="modal-value"> {{ nextPaymentDueDate }} </span>
    </div>
    <hr class="modal-divider" />
    <div class="modal-row" v-if="isBankTransfer">
      <span class="modal-label">
        {{ t("BANK_ACCOUNT") }}
      </span>
      <span class="modal-value"> {{ bankAccountNumber }} </span>
    </div>
    <!--/////ESTIMATE SUMMARY/////-->
    <CollapseCard
      :title="t('CL_WITHDRAWAL.WITHDRAWAL_DETAILS_TITLE')"
      :collapsable="true"
      fill
      flat-style
      class="estimate-summary"
      title-font-size="13px"
    >
      <div class="details-content">
        <div class="details-row">
          <span class="details-label">
            {{ t("CL_WITHDRAWAL.WITHDRAW_AMOUNT") }}
          </span>
          <span class="details-value">{{ amount }}</span>
        </div>
        <div class="details-row">
          <span class="details-label">
            {{ t("EXPLAINERRATE") }}
          </span>
          <span class="details-value">{{ annualInterestRate }}%</span>
        </div>
        <slot v-if="withdrawalEstimateFields">
          <div
            v-for="field in estimateFieldsArray"
            :key="field.name"
            class="details-row"
          >
            <span class="details-label">
              {{ t(field.stringKey) }}
            </span>
            <span class="details-value"> {{ field.value }}% </span>
          </div>
        </slot>
        <div class="details-row">
          <span class="details-label">
            {{ t("CL_WITHDRAWAL.WITHDRAWAL_MAXIMUM_AMOUNT_TO_PAY") }}
            <IconBannerTooltip
              :bannerText="
                t('CL_WITHDRAWAL.WITHDRAWAL_TOOLTIP_IF_YOU_ALWAYS_PAY', {
                  amount: weeklyPaymentAmount,
                })
              "
              banner-width="177px"
              @on-click="onTooltipClicked"
            />
          </span>
          <span class="details-value">
            {{ withdrawalTotalPayment }}
          </span>
        </div>
      </div>
    </CollapseCard>
    <Alert
      class="green-alert"
      alert-image="img/credit-line/credit-card/trending_down.svg"
      alert-type="alert-primary"
      :closable="false"
      :message="
        t('CL_WITHDRAWAL.WITHDRAWAL_PAY_MORE_THAN_WEEKLY', {
          amount: weeklyPaymentAmount,
        })
      "
      message-font-weight="400"
      :message-font-style="{
        lineHeight: '1.12em !important',
        letterSpacing: '-0.2px !important',
        overflowWrap: 'anywhere',
        wordBreak: 'break-word',
        whiteSpace: 'normal',
        fontSize: '13px !important',
      }"
      :message-lines="2"
    />
  </div>
</template>

<script setup lang="ts">
import { useI18n } from "@/apps/credit-line/composables/useI18n";
import CollapseCard from "@/apps/credit-line/components/CollapseCard.vue";
import IconBannerTooltip from "@/apps/credit-line/components/IconBannerTooltip.vue";
import Alert from "@/shared-components/common/Alert.vue";
import { computed } from "vue";
import {
  IFeeSchema,
  IWithdrawalEstimateField,
} from "@/apps/credit-line/models/IWithdrawalEstimate";
import { formatNumber } from "@/apps/credit-line/helpers/formatNumber.helper";
import logAnalyticsEvent from "@/apps/credit-line/services/google-analytics-events.service";
import GoogleAnalyticsEventsHelper from "@/shared-helpers/google-analytics-events.helper";

const { t } = useI18n();

const props = defineProps<{
  paymentTerm: number;
  amount: string;
  annualInterestRate: string;
  weeklyPaymentAmount: string;
  nextPaymentDueDate: string;
  withdrawalTotalPayment: string;
  isBankTransfer: boolean;
  bankAccountNumber?: number | string;
  feeSchema?: IFeeSchema;
  withdrawalEstimateFields?: IWithdrawalEstimateField[];
}>();

const estimateFieldsArray = computed(() => {
  return (props.withdrawalEstimateFields ?? []).map((field) => ({
    ...field,
    value: formatNumber(
      props.feeSchema?.[field.name]?.[field.displayValueName] * 100 ?? 0
    ),
  }));
});

const onTooltipClicked = () => {
  logAnalyticsEvent(
    GoogleAnalyticsEventsHelper.CreditLine.WithdrawalEstimateToolTipViewed,
    "CL Withdrawal New UI"
  );
};
</script>

<style lang="scss" scoped>
.modal-payment-plan {
  width: 100%;
  margin: 0 auto;
}
.modal-title {
  color: var(--Typography-Text-1, #111);
  text-align: center;
  font-feature-settings: "liga" off, "clig" off;
  font-family: Inter, serif;
  font-size: 20px;
  font-style: normal;
  font-weight: 600;
  line-height: 29px;
}
.modal-divider {
  color: gray;
  margin: 8px 0 16px 0;
}
.modal-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 16px;
  margin-bottom: 8px;
}
.modal-label {
  color: var(--Typography-Text-2-ICON, #595a5a);
  font-feature-settings: "liga" off, "clig" off;
  font-family: Inter, serif;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 21px;
}
.modal-value {
  color: var(--Typography-Text-1, #111);
  text-align: right;
  font-feature-settings: "liga" off, "clig" off;
  font-family: Inter, serif;
  font-size: 14px;
  font-style: normal;
  font-weight: 500;
  line-height: 21px;
}

.details-content {
  margin-top: 0;
  padding: 0;
}

.details-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 0.5rem;
  font-family: Inter, Arial, sans-serif;
}

.details-label {
  color: var(--Typography-Text-2-ICON, #595a5a);
  font-feature-settings: "liga" off, "clig" off;
  font-family: Inter, serif;
  font-size: 13px;
  font-style: normal;
  font-weight: 400;
  line-height: 17px;
}

.details-value {
  color: var(--Typography-Text-2-ICON, #595a5a);
  text-align: right;
  font-feature-settings: "liga" off, "clig" off;
  font-family: Inter, serif;
  font-size: 13px;
  font-style: normal;
  font-weight: 400;
  line-height: 17px;
}

.estimate-summary {
  margin-top: 12px;
}

.green-alert {
  margin-top: 12px;
}
</style>
